import { EnlargedSearchResultContent, SquareReturnIcon } from '@atlassian/search-dialog';
import { ConfluenceIcon } from '@atlaskit/logo';
import React, { type ReactElement, useMemo } from 'react';
import { FormattedMessage } from 'react-intl-next';
import { messages } from '../../messages';
import {
	type SearchResult,
	type SearchResultSection,
	useProductContext,
} from '../../../product-router';
import { useQuery } from '../../../query-context';
import { useAdvancedSearchAnalyticsCallbacks } from '../../../search-result';
import {
	AdditionalMetaIconSpacer,
	AdditionalMetaTextWrapper,
	AdditionalMetaWrapper,
} from './sections.styled';
import { PersistRecentSearch } from '../../../recent-searches/persist-recent-search';
import { CommandIcon, ControlIcon } from '../../icons';

export const SHOW_FILTERED_RESULTS_SECTION_ID = 'show-filtered-results';

const AdditionalMeta: React.FC<{}> = () => {
	const ModifierKeyIcon = useMemo(
		() => (window.navigator.userAgent.includes('Mac') ? CommandIcon : ControlIcon),
		[],
	);

	return (
		<AdditionalMetaWrapper>
			<AdditionalMetaTextWrapper>
				<FormattedMessage {...messages.quick_find_show_filtered_results} />
			</AdditionalMetaTextWrapper>
			<AdditionalMetaIconSpacer>
				<ModifierKeyIcon />
				<SquareReturnIcon />
			</AdditionalMetaIconSpacer>
		</AdditionalMetaWrapper>
	);
};

export const useShowFilteredResultsSection = (): SearchResultSection => {
	const { query } = useQuery();
	const { getActiveProduct } = useProductContext();
	const advancedSearchUrl = getActiveProduct()?.generateAdvancedSearchUrl?.(query);

	// TODO: we should pre-select product filter in FPS based on primaryProduct value in QF
	const showFilteredResultsSection: SearchResultSection = useMemo(() => {
		const showFilteredResultsItem: SearchResult = {
			id: SHOW_FILTERED_RESULTS_SECTION_ID,
			url: advancedSearchUrl ? `${advancedSearchUrl}&product=confluence` : '',
			title: query,
			meta: '',
			additionalMeta: <AdditionalMeta />,
			icon: <ConfluenceIcon appearance="brand" size="small" />,
		};

		return {
			id: SHOW_FILTERED_RESULTS_SECTION_ID,
			title: '',
			searchResults: [showFilteredResultsItem],
			resultRenderer: ({ searchResult, isKeyboardHighlighted, isCollapsed }) => (
				<PersistRecentSearch>
					<EnlargedSearchResultContent
						{...searchResult}
						icon={searchResult.icon as ReactElement}
						isKeyboardHighlighted={isKeyboardHighlighted}
						isCollapsed={isCollapsed}
						returnIcon="never"
					/>
				</PersistRecentSearch>
			),
			useAnalyticsOverride: () =>
				useAdvancedSearchAnalyticsCallbacks({
					actionSubjectId: 'showFilteredResults',
				}),
		};
	}, [advancedSearchUrl, query]);

	return showFilteredResultsSection;
};
